/**
 * The ribbon over the document, Word's: tabs Home (styles, font, size, bold..., colours,
 * alignment, lists, indent, undo), Insert (table, picture, page break, mention, comment), Layout
 * (paper, margins, orientation), Review (comments, what a merge kept from someone else,
 * printing, saving as a template), View (the page light or dark, apart from the app) and, while a
 * picture is selected, Picture (how it sits among the words, its size and caption). Every control
 * acts on the editor through `commands.ts`.
 */
import { For, Show, createMemo, createSignal, type JSX } from "solid-js";
import type { Orientation } from "@protocol/Orientation";
import type { PaperSize } from "@protocol/PaperSize";
import type { Command } from "prosemirror-state";
import { redo, undo } from "prosemirror-history";
import { useApp } from "../../../app/context";
import { DOC_FONTS, DOC_SIZES, MARGINS, PAPER_LABELS, PICTURE_ALIGNS, WIDTH_RANGE, WRAPS, docSchema } from "../../../state/documents";
import { toast } from "../../../shared/toast";
import {
  STYLES,
  TABLE_COMMANDS,
  addComment,
  align,
  alignAt,
  clearColours,
  commentsOf,
  conflictsOf,
  goTo,
  inTable,
  indent,
  insertImage,
  insertMention,
  insertPageBreak,
  insertTable,
  list,
  listAt,
  markActive,
  markAt,
  pictureAt,
  removeComment,
  setMark,
  setPicture,
  setStyle,
  settle,
  styleAt,
  toggle,
  type StyleId,
} from "./commands";
import type { EditorParts } from "./Editor";
import { DOC_LOOKS, docLook, setDocLook, type DocLook } from "./look";

/** The ribbon's tabs. */
const TABS = [
  { id: "home", label: "Home" },
  { id: "insert", label: "Insert" },
  { id: "layout", label: "Layout" },
  { id: "review", label: "Review" },
  { id: "view", label: "View" },
  { id: "picture", label: "Picture" },
] as const;

/** A ribbon tab's id. */
type TabId = (typeof TABS)[number]["id"];

/** The ribbon. */
export function Ribbon(props: { parts: EditorParts }): JSX.Element {
  const { parts } = props;
  const { assets, graph, presence } = useApp();
  const [tab, setTab] = createSignal<TabId>("home");
  const [comment, setComment] = createSignal("");
  const [templateName, setTemplateName] = createSignal("");
  const [mention, setMention] = createSignal("");

  /** Runs `command` on the editor, then gives it the keyboard back. */
  const run = (command: Command) => {
    const v = parts.view();
    if (!v) return false;
    const done = command(v.state, v.dispatch);
    v.focus();
    return done;
  };
  /** The editor's state, followed on every change. */
  const state = () => {
    parts.tick();
    return parts.view()?.state ?? null;
  };
  const on = (name: "strong" | "em" | "underline" | "strike" | "code") => {
    const s = state();
    return !!s && markActive(s, docSchema.marks[name]!);
  };
  const markValue = (name: "font" | "size" | "color" | "highlight", attr: string) => {
    const s = state();
    const mark = s ? markAt(s, docSchema.marks[name]!) : null;
    return mark ? String(mark.attrs[attr]) : "";
  };
  const comments = createMemo(() => {
    const s = state();
    return s ? commentsOf(s.doc) : [];
  });
  const conflicts = createMemo(() => {
    const s = state();
    return s ? conflictsOf(s.doc) : [];
  });
  const pictures = createMemo(() => (assets.source("project")?.listing.files ?? []).filter((f) => f.kind === "image"));
  const disabled = () => !parts.canEdit() || !parts.view();
  /** The picture selected (`null`: none). */
  const picture = createMemo(() => {
    const s = state();
    return s ? pictureAt(s) : null;
  });
  /** The tab shown: Picture only while a picture is selected. */
  const shownTab = () => (tab() === "picture" && !picture() ? "home" : tab());

  /** A toggle button of the ribbon. */
  const Toggle = (p: { cmd: string; label: string; pressed?: () => boolean; command: Command; children: JSX.Element }) => (
    <button type="button" class="doc-btn" data-doc-cmd={p.cmd} title={p.label} aria-label={p.label} aria-pressed={p.pressed ? p.pressed() : undefined} disabled={disabled()} onMouseDown={(e) => e.preventDefault()} onClick={() => run(p.command)}>
      {p.children}
    </button>
  );

  return (
    <div class="doc-ribbon" id="doc-ribbon">
      <div class="doc-tabs" role="tablist">
        <button type="button" class="doc-tab doc-back" id="doc-back" title="Every document, and new pages from the templates" onClick={() => parts.back()}>
          ← Documents
        </button>
        <For each={TABS.filter((t) => t.id !== "picture" || picture())}>
          {(t) => (
            <button type="button" role="tab" class={`doc-tab${t.id === "picture" ? " contextual" : ""}`} data-doc-tab={t.id} aria-selected={shownTab() === t.id} onClick={() => setTab(t.id)}>
              {t.label}
              <Show when={t.id === "review" && conflicts().length}>
                <span class="doc-badge">{conflicts().length}</span>
              </Show>
            </button>
          )}
        </For>
      </div>
      <div class="doc-tools">
        <Show when={shownTab() === "home"}>
          <div class="doc-group">
            <Toggle cmd="undo" label="Undo (Ctrl+Z)" command={undo}>↶</Toggle>
            <Toggle cmd="redo" label="Redo (Ctrl+Y)" command={redo}>↷</Toggle>
          </div>
          <div class="doc-group">
            <select id="doc-style" aria-label="Paragraph style" disabled={disabled()} value={state() ? styleAt(state()!) : "normal"} onChange={(e) => run(setStyle(e.currentTarget.value as StyleId))}>
              <For each={STYLES}>{(s) => <option value={s.id}>{s.label}</option>}</For>
            </select>
            <select id="doc-font" aria-label="Font" disabled={disabled()} value={markValue("font", "family")} onChange={(e) => run(setMark(docSchema.marks.font!, e.currentTarget.value ? { family: e.currentTarget.value } : null))}>
              <option value="">Default font</option>
              <For each={Object.keys(DOC_FONTS)}>{(f) => <option value={f}>{f}</option>}</For>
            </select>
            <select id="doc-size" aria-label="Size" disabled={disabled()} value={markValue("size", "size")} onChange={(e) => run(setMark(docSchema.marks.size!, e.currentTarget.value ? { size: Number(e.currentTarget.value) } : null))}>
              <option value="">Size</option>
              <For each={DOC_SIZES}>{(s) => <option value={String(s)}>{s}</option>}</For>
            </select>
          </div>
          <div class="doc-group">
            <Toggle cmd="bold" label="Bold (Ctrl+B)" pressed={() => on("strong")} command={toggle("strong")}>
              <b>B</b>
            </Toggle>
            <Toggle cmd="italic" label="Italic (Ctrl+I)" pressed={() => on("em")} command={toggle("em")}>
              <i>I</i>
            </Toggle>
            <Toggle cmd="underline" label="Underline (Ctrl+U)" pressed={() => on("underline")} command={toggle("underline")}>
              <u>U</u>
            </Toggle>
            <Toggle cmd="strike" label="Strikethrough" pressed={() => on("strike")} command={toggle("strike")}>
              <s>S</s>
            </Toggle>
            <Toggle cmd="code" label="Code" pressed={() => on("code")} command={toggle("code")}>
              {"</>"}
            </Toggle>
            <label class="doc-colour" title="Text colour">
              A
              <input id="doc-color" type="color" aria-label="Text colour" disabled={disabled()} value={markValue("color", "color") || "#000000"} onInput={(e) => run(setMark(docSchema.marks.color!, { color: e.currentTarget.value }))} />
            </label>
            <label class="doc-colour highlight" title="Highlight">
              ▮
              <input id="doc-highlight" type="color" aria-label="Highlight" disabled={disabled()} value={markValue("highlight", "color") || "#fff59d"} onInput={(e) => run(setMark(docSchema.marks.highlight!, { color: e.currentTarget.value }))} />
            </label>
            <Toggle cmd="clear-colour" label="No colour nor highlight" command={clearColours()}>
              ⌀
            </Toggle>
          </div>
          <div class="doc-group">
            <For each={["left", "center", "right", "justify"] as const}>
              {(a) => (
                <Toggle cmd={`align-${a}`} label={`Align ${a}`} pressed={() => !!state() && alignAt(state()!) === a} command={align(a)}>
                  <AlignIcon align={a} />
                </Toggle>
              )}
            </For>
          </div>
          <div class="doc-group">
            <Toggle cmd="bullets" label="Bulleted list" pressed={() => !!state() && listAt(state()!) === "bullet_list"} command={list("bullet_list")}>
              •≡
            </Toggle>
            <Toggle cmd="numbers" label="Numbered list" pressed={() => !!state() && listAt(state()!) === "ordered_list"} command={list("ordered_list")}>
              1≡
            </Toggle>
            <Toggle cmd="outdent" label="Decrease indent (Shift+Tab in a list)" command={indent(-1)}>
              ⇤
            </Toggle>
            <Toggle cmd="indent" label="Increase indent (Tab in a list)" command={indent(1)}>
              ⇥
            </Toggle>
          </div>
        </Show>

        <Show when={shownTab() === "insert"}>
          <div class="doc-group">
            <Toggle cmd="table" label="Insert a table (3 × 3)" command={insertTable()}>
              ▦ Table
            </Toggle>
            <Show when={state() && inTable(state()!)}>
              <For each={TABLE_COMMANDS}>
                {(c) => (
                  <Toggle cmd={`table-${c.id}`} label={c.label} command={c.command}>
                    {c.label}
                  </Toggle>
                )}
              </For>
            </Show>
          </div>
          <div class="doc-group">
            <select
              id="doc-insert-image"
              aria-label="Insert a picture from the project's files"
              disabled={disabled() || !pictures().length}
              value=""
              onChange={(e) => {
                const path = e.currentTarget.value;
                e.currentTarget.value = "";
                if (path) run(insertImage(path));
              }}
            >
              <option value="">{pictures().length ? "Picture…" : "No pictures in the project"}</option>
              <For each={pictures()}>{(f) => <option value={f.path} data-doc-image={f.path}>{f.path}</option>}</For>
            </select>
            <Toggle cmd="page-break" label="Page break (Ctrl+Enter)" command={insertPageBreak()}>
              ⤓ Page break
            </Toggle>
          </div>
          <div class="doc-group">
            <input id="doc-mention" list="doc-mention-names" placeholder="Mention… ([[Name]])" aria-label="Mention a node" disabled={disabled()} value={mention()} onInput={(e) => setMention(e.currentTarget.value)} onKeyDown={(e) => e.key === "Enter" && insertTheMention()} />
            <datalist id="doc-mention-names">
              <For each={graph.state.graph.nodes}>{(n) => <option value={n.name} />}</For>
            </datalist>
            <button type="button" class="doc-btn" data-doc-cmd="mention" disabled={disabled() || !mention().trim()} onClick={insertTheMention}>
              Mention
            </button>
          </div>
          <div class="doc-group">
            <input id="doc-comment-text" placeholder="Comment on the selected words…" aria-label="Comment" disabled={disabled()} value={comment()} onInput={(e) => setComment(e.currentTarget.value)} onKeyDown={(e) => e.key === "Enter" && addTheComment()} />
            <button type="button" class="doc-btn" data-doc-cmd="comment" disabled={disabled() || !comment().trim()} onMouseDown={(e) => e.preventDefault()} onClick={addTheComment}>
              Comment
            </button>
          </div>
        </Show>

        <Show when={shownTab() === "layout"}>
          <div class="doc-group">
            <label>
              Paper
              <select id="doc-paper" disabled={disabled()} value={parts.layout().size} onChange={(e) => parts.setLayout({ ...parts.layout(), size: e.currentTarget.value as PaperSize })}>
                <For each={Object.entries(PAPER_LABELS)}>{([id, label]) => <option value={id}>{label}</option>}</For>
              </select>
            </label>
            <label>
              Margins
              <select
                id="doc-margins"
                disabled={disabled()}
                value={MARGINS.find((m) => m.margins.join() === parts.layout().margins.join())?.id ?? "custom"}
                onChange={(e) => {
                  const preset = MARGINS.find((m) => m.id === e.currentTarget.value);
                  if (preset) parts.setLayout({ ...parts.layout(), margins: [...preset.margins] });
                }}
              >
                <For each={MARGINS}>{(m) => <option value={m.id}>{m.label}</option>}</For>
                <Show when={!MARGINS.some((m) => m.margins.join() === parts.layout().margins.join())}>
                  <option value="custom">Custom ({parts.layout().margins.join(", ")} mm)</option>
                </Show>
              </select>
            </label>
            <label>
              Orientation
              <select id="doc-orientation" disabled={disabled()} value={parts.layout().orientation} onChange={(e) => parts.setLayout({ ...parts.layout(), orientation: e.currentTarget.value as Orientation })}>
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </label>
          </div>
          <div class="doc-group">
            <button type="button" class="doc-btn" id="doc-print" onClick={() => parts.print()}>
              🖶 Print or PDF
            </button>
          </div>
        </Show>

        <Show when={shownTab() === "review"}>
          <div class="doc-review">
            <section>
              <h4>Comments</h4>
              <Show when={comments().length} fallback={<p class="muted">No comment. Select words, then Insert → Comment.</p>}>
                <For each={comments()}>
                  {(c) => (
                    <div class="doc-comment-row" data-doc-comment={c.id}>
                      <button type="button" class="linklike" onClick={() => run(goTo(c.from - 1))}>
                        “{c.quote.length > 40 ? `${c.quote.slice(0, 40)}…` : c.quote}”
                      </button>
                      <span>
                        <b>{c.by || "Someone"}</b>: {c.text}
                      </span>
                      <button type="button" class="doc-btn" data-doc-comment-remove={c.id} disabled={disabled()} onClick={() => run(removeComment(c.id))}>
                        Resolve
                      </button>
                    </div>
                  )}
                </For>
              </Show>
            </section>
            <section>
              <h4>Changed by two people</h4>
              <Show when={conflicts().length} fallback={<p class="muted">Nothing to settle: when two people change the same paragraph, the other version shows here.</p>}>
                <For each={conflicts()}>
                  {(c, i) => (
                    <div class="doc-conflict-row" data-doc-conflict={i()}>
                      <button type="button" class="linklike" onClick={() => run(goTo(c.pos))}>
                        {c.text.slice(0, 60) || "(empty)"}
                      </button>
                      <button type="button" class="doc-btn" data-doc-keep="theirs" disabled={disabled()} onClick={() => run(settle(c.pos, "theirs"))}>
                        Keep theirs
                      </button>
                      <button type="button" class="doc-btn" data-doc-keep="ours" disabled={disabled()} onClick={() => run(settle(c.pos, "ours"))}>
                        Keep mine
                      </button>
                    </div>
                  )}
                </For>
              </Show>
            </section>
            <section>
              <h4>Template</h4>
              <div class="doc-group">
                <input id="doc-template-name" placeholder="Template name" aria-label="Template name" disabled={disabled()} value={templateName()} onInput={(e) => setTemplateName(e.currentTarget.value)} />
                <button
                  type="button"
                  class="doc-btn"
                  id="doc-save-template"
                  disabled={disabled() || !templateName().trim()}
                  onClick={() =>
                    void parts.saveTemplate(templateName()).then((ok) => {
                      if (ok) {
                        toast(`Template "${templateName().trim()}" kept with the project.`);
                        setTemplateName("");
                      }
                    })
                  }
                >
                  Save as template
                </button>
              </div>
            </section>
          </div>
        </Show>

        <Show when={shownTab() === "view"}>
          <div class="doc-group">
            <label>
              Page colour
              <select id="doc-look" value={docLook()} onChange={(e) => setDocLook(e.currentTarget.value as DocLook)}>
                <For each={DOC_LOOKS}>{(l) => <option value={l.id}>{l.label}</option>}</For>
              </select>
            </label>
            <span class="muted small">Printing is always on white paper.</span>
          </div>
        </Show>

        <Show when={shownTab() === "picture" && picture()}>
          {(p) => (
            <>
              <div class="doc-group" role="radiogroup" aria-label="Wrap text">
                <For each={WRAPS}>
                  {(w) => (
                    <button type="button" class="doc-btn" data-doc-wrap={w.id} aria-pressed={p().look.wrap === w.id} disabled={disabled()} onMouseDown={(e) => e.preventDefault()} onClick={() => run(setPicture(p().pos, { wrap: w.id }))}>
                      {w.label}
                    </button>
                  )}
                </For>
              </div>
              <div class="doc-group">
                <For each={PICTURE_ALIGNS}>
                  {(a) => (
                    <Toggle cmd={`picture-${a}`} label={p().look.wrap === "square" ? `Words wrap on the ${a === "right" ? "left" : "right"}` : `Picture ${a === "center" ? "centred" : `on the ${a}`}`} pressed={() => p().look.align === a} command={setPicture(p().pos, { align: a })}>
                      <AlignIcon align={a} />
                    </Toggle>
                  )}
                </For>
              </div>
              <div class="doc-group">
                <label>
                  Width
                  <input
                    id="doc-image-width"
                    type="number"
                    min={WIDTH_RANGE[0]}
                    max={WIDTH_RANGE[1]}
                    step="5"
                    disabled={disabled()}
                    value={p().look.width}
                    onChange={(e) => run(setPicture(p().pos, { width: Number(e.currentTarget.value) }))}
                  />
                  %
                </label>
                <label>
                  Caption
                  <input id="doc-image-alt" placeholder="Words under the picture" disabled={disabled()} value={String(p().node.attrs.alt ?? "")} onChange={(e) => run(setPicture(p().pos, { alt: e.currentTarget.value }))} />
                </label>
              </div>
            </>
          )}
        </Show>
      </div>
    </div>
  );

  /** Writes the mention typed at the caret. */
  function insertTheMention() {
    const name = mention().trim();
    if (!name) return;
    const node = graph.state.graph.nodes.find((n) => n.name.toLowerCase() === name.toLowerCase());
    run(insertMention(node?.name ?? name, node?.id ?? null));
    setMention("");
  }

  /** Puts the comment typed on the selected words. */
  function addTheComment() {
    if (!run(addComment(comment(), presence.me().name))) return void toast("Select the words to comment on first.", "error");
    setComment("");
  }
}

/** A little picture of an alignment: lines of text, lined up. */
function AlignIcon(props: { align: "left" | "center" | "right" | "justify" }): JSX.Element {
  const short = (y: number) => {
    const w = props.align === "justify" ? 12 : 8;
    const x = props.align === "left" || props.align === "justify" ? 2 : props.align === "right" ? 14 - w : 8 - w / 2;
    return <path d={`M${x} ${y}h${w}`} />;
  };
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
      <path d="M2 3h12M2 9h12" />
      {short(6)}
      {short(12)}
    </svg>
  );
}
